import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const slides = [
  {
    id: 1,
    title: 'Trade Fair Stall Building',
    subtitle: 'Custom Exhibition Booths',
    description: 'Stunning exhibition booths that make your brand stand out at trade fairs worldwide.',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=2000&q=80',
    link: '/services/stall-building'
  },
  {
    id: 2,
    title: 'Corporate Events',
    subtitle: 'Conferences & Summits',
    description: 'End-to-end corporate event management from conferences to product launches.',
    image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=2000&q=80',
    link: '/services/corporate-events'
  },
  {
    id: 3,
    title: 'Live Events',
    subtitle: 'Marathons & Galas',
    description: 'Spectacular live experiences from marathons to corporate galas that inspire and unite.',
    image: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&w=2000&q=80',
    link: '/services/live-events'
  },
  {
    id: 4,
    title: 'Brand Activation',
    subtitle: 'Experiential Marketing',
    description: 'Strategic brand experiences that engage consumers and drive measurable results.',
    image: 'https://images.pexels.com/photos/29708240/pexels-photo-29708240.jpeg?auto=compress&w=2000',
    link: '/services/brand-activation'
  }
];

export const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Slideshow */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0 z-0"
        >
          <img
            src={slides[currentSlide].image}
            alt={slides[currentSlide].title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40 w-full">
        <div className="max-w-3xl">
          {/* Badge */}
          <motion.div
            key={`badge-${currentSlide}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 px-4 py-2 mb-8"
          >
            <span className="w-2 h-2 bg-gold rounded-full animate-pulse" />
            <span className="text-gold text-sm font-medium uppercase tracking-wider">
              {slides[currentSlide].subtitle}
            </span>
          </motion.div>

          {/* Heading */}
          <AnimatePresence mode="wait">
            <motion.h1
              key={`title-${currentSlide}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              className="font-heading text-4xl md:text-5xl lg:text-7xl text-white leading-tight mb-6"
            >
              {slides[currentSlide].title}
            </motion.h1>
          </AnimatePresence>

          {/* Description */}
          <AnimatePresence mode="wait">
            <motion.p
              key={`desc-${currentSlide}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-white/80 text-lg md:text-xl leading-relaxed mb-4 max-w-2xl"
            >
              {slides[currentSlide].description}
            </motion.p>
          </AnimatePresence>

          {/* Company tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gold/90 text-sm md:text-base mb-10 max-w-2xl"
          >
            DFAIRS Communications — 15+ Years of Excellence in Event Management
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center justify-center gap-2 group"
              data-testid="hero-cta-primary"
            >
              Start Your Project
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to={slides[currentSlide].link}
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-semibold px-8 py-4 uppercase tracking-wider hover:bg-white hover:text-navy transition-colors"
              data-testid="hero-cta-secondary"
            >
              <Play className="w-5 h-5" />
              Explore Service
            </Link>
          </motion.div>
        </div>

        {/* Slide Navigation */}
        <div className="absolute bottom-12 left-4 sm:left-6 lg:left-8 right-4 sm:right-6 lg:right-8 flex items-center justify-between">
          {/* Dots */}
          <div className="flex items-center gap-3">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 ${
                  index === currentSlide 
                    ? 'w-12 h-2 bg-gold' 
                    : 'w-2 h-2 bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Go to slide ${index + 1}`}
                data-testid={`slide-dot-${index}`}
              />
            ))}
          </div>

          {/* Arrows */}
          <div className="flex items-center gap-2">
            <button
              onClick={prevSlide}
              className="w-12 h-12 border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-navy transition-colors"
              aria-label="Previous slide"
              data-testid="slide-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="w-12 h-12 border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-navy transition-colors"
              aria-label="Next slide"
              data-testid="slide-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Service Labels - Desktop */}
        <div className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col gap-4">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => goToSlide(index)}
              className={`text-right px-4 py-2 border-r-2 transition-all duration-300 ${
                index === currentSlide 
                  ? 'border-gold text-white' 
                  : 'border-transparent text-white/50 hover:text-white/80'
              }`}
              data-testid={`service-label-${index}`}
            >
              <span className="text-sm uppercase tracking-wider">{slide.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Decorative Element */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10" />
    </section>
  );
};

export default HeroSection;
